import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { Link2 } from "lucide-react";
import toast from "react-hot-toast";

interface TestDoc {
  jobTitle?: string;
  duration?: number;
  questionCount?: number;
  config?: {
    difficulty: string;
    duration: number;
    questionCount: number;
  };
  testData?: {
    title?: string;
    description?: string;
    sections?: Array<{
      name: string;
      questions: number;
      timeLimit: number;
      skills: string[];
    }>;
    sampleQuestions?: Array<{
      id: number;
      question: string;
      options: string[];
      correctAnswer: number;
      difficulty: string;
      skill: string;
    }>;
  };
  createdAt?: any;
}

const formatDate = (timestamp: any) => {
  if (!timestamp) return "";
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleString();
};

const TestDetails: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<TestDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const shareUrl = useMemo(() => `${window.location.origin}/test/share/${testId}`, [testId]);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch (error) {
      toast.error("Couldn't copy link. Please copy manually.");
    }
  };

  useEffect(() => {
    if (!testId) return;

    const fetchTest = async () => {
      try {
        const db = getFirestore(firebaseApp);
        const docRef = doc(db, "hrTests", testId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setTest(snap.data() as TestDoc);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId]);

  if (loading) {
    return <div className="text-center py-10">Loading test details...</div>;
  }

  if (!test) {
    return (
      <div className="text-center py-10">
        Test not found.
        <div className="mt-4">
          <Link to="/hr/my-tests" className="text-red-700 hover:underline">
            Back to My Tests
          </Link>
        </div>
      </div>
    );
  }

  const { testData } = test;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {testData?.title || test.jobTitle || "Test Details"}
          </h1>
          <p className="text-sm text-gray-600 mt-1">Share this test with candidates.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyShareLink}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
          >
            <Link2 className="w-4 h-4" />
            Copy Share Link
          </button>
          <Link
            to="/hr/my-tests"
            className="text-red-700 hover:underline text-sm"
          >
            Back to My Tests
          </Link>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4 shadow-sm">
        <p className="text-gray-700">
          <strong>Generated on:</strong> {formatDate(test.createdAt)}
        </p>
        <p className="text-gray-700">
          <strong>Position:</strong> {test.jobTitle}
        </p>
        <p className="text-gray-700">
          <strong>Difficulty:</strong> {test.config?.difficulty}
        </p>
        <p className="text-gray-700">
          <strong>Duration:</strong> {test.config?.duration} min
        </p>
        <p className="text-gray-700">
          <strong>Total Questions:</strong> {test.config?.questionCount}
        </p>
      </div>

      {/* Sections overview */}
      {testData?.sections?.length ? (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-900">Sections</h2>
          {testData.sections.map((section, idx) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-lg p-4 flex justify-between"
            >
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  {section.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {section.questions} questions • {section.timeLimit} min
                </p>
              </div>
              <div className="flex flex-wrap gap-2 self-center">
                {(section.skills || []).map((skill, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Questions */}
      {testData?.sampleQuestions?.length ? (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-gray-900">Questions</h2>
          {testData.sampleQuestions.map((q, idx) => (
            <div
              key={q.id}
              className="border border-gray-200 rounded-lg p-6 space-y-3"
            >
              <div className="flex items-center space-x-3">
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                  Q{idx + 1}
                </span>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                  {q.skill}
                </span>
                <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full capitalize">
                  {q.difficulty}
                </span>
              </div>
              <h4 className="text-gray-900 font-medium">{q.question}</h4>
              <div className="space-y-2">
                {(q.options || []).map((opt, optIdx) => (
                  <div
                    key={optIdx}
                    className={`p-3 rounded-lg border ${
                      optIdx + 1 === q.correctAnswer
                        ? "border-green-300 bg-green-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <span className="text-sm">
                      {String.fromCharCode(65 + optIdx)}. {opt}
                    </span>
                    {optIdx + 1 === q.correctAnswer && (
                      <span className="ml-2 text-green-600 text-xs">
                        ✓ Correct
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TestDetails;
